import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import NumerologyReportForm from '../components/numerology/NumerologyReportForm';
import NumerologyReportView from '../components/numerology/NumerologyReportView';
import { buildSymbolicProfileReport } from '../lib/symbolicProfile';
import { getSamplePersonaInput, SAMPLE_PERSONA_IDS } from '../lib/numerology/samplePersonas';
import { PROFILE_KINDS } from '../lib/symbolicProfile/types';
import '../components/numerology/NumerologyReportEnginePage.css';

const PAGE_TITLE = 'Your report | Numerology | Astral Moon Magic';

const initialValues = () => {
  const y = new Date().getFullYear();
  return {
    firstName: '',
    fullBirthName: '',
    dateOfBirth: '',
    currentName: '',
    targetYear: String(y),
    preferredLifeArea: '',
  };
};

function runReportFromInput(input) {
  return buildSymbolicProfileReport(
    { ...input, profileKind: input.profileKind || PROFILE_KINDS.NUMEROLOGY_ONLY },
    {
      sampleMode: input.sampleMode,
      samplePersonaId: input.samplePersonaId,
    },
  );
}

export default function NumerologyReportEnginePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sampleParam = searchParams.get('sample');
  const isSampleViewerMode = Boolean(sampleParam && String(sampleParam).trim() !== '');
  const isKnownSample =
    isSampleViewerMode && Object.values(SAMPLE_PERSONA_IDS).includes(sampleParam);

  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [activeSampleId, setActiveSampleId] = useState(null);
  const lastAutoSampleFromUrl = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  const onChange = useCallback((name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
  }, []);

  const generateSample = useCallback((personaId) => {
    setError('');
    setSubmitting(true);
    setResult(null);
    setActiveSampleId(personaId);

    window.setTimeout(() => {
      try {
        const input = getSamplePersonaInput(personaId);
        if (!input) {
          setError('Unknown sample.');
          setSubmitting(false);
          return;
        }
        const built = runReportFromInput(input);
        if (!built.ok) {
          setError(built.error || 'Something went wrong.');
          setSubmitting(false);
          return;
        }
        setResult(built);
      } catch (err) {
        setError(err?.message || 'Something went wrong while building the sample report.');
      } finally {
        setSubmitting(false);
      }
    }, 30);
  }, []);

  useEffect(() => {
    const sample = searchParams.get('sample');
    if (!sample) {
      lastAutoSampleFromUrl.current = null;
      return;
    }
    const valid = Object.values(SAMPLE_PERSONA_IDS).includes(sample);
    if (!valid) {
      setError('This sample is not available.');
      setResult(null);
      setActiveSampleId(null);
      setSubmitting(false);
      return;
    }
    setError('');
    if (lastAutoSampleFromUrl.current === sample) return;
    lastAutoSampleFromUrl.current = sample;
    generateSample(sample);
  }, [searchParams, generateSample]);

  const switchSample = useCallback(
    (personaId) => {
      const next = new URLSearchParams(searchParams);
      next.set('sample', personaId);
      next.delete('tier');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setError('');
      const firstName = String(values.firstName || '').trim();
      const fullBirthName = String(values.fullBirthName || '').trim();
      const dateOfBirth = String(values.dateOfBirth || '').trim();
      if (!firstName) {
        setError('Please enter your first name.');
        return;
      }
      if (!fullBirthName) {
        setError('Please enter your full birth name (as on your birth record).');
        return;
      }
      if (!dateOfBirth) {
        setError('Please choose your date of birth.');
        return;
      }

      setSubmitting(true);
      setResult(null);
      setActiveSampleId(null);

      window.setTimeout(() => {
        try {
          const input = {
            firstName,
            fullBirthName,
            dateOfBirth,
            currentName: values.currentName,
            targetYear: values.targetYear ? Number(values.targetYear) : undefined,
            preferredLifeArea: values.preferredLifeArea,
            sampleMode: false,
            profileKind: PROFILE_KINDS.NUMEROLOGY_ONLY,
          };
          const built = runReportFromInput(input);
          if (!built.ok) {
            setError(built.error || 'The report could not be built from those details.');
            setSubmitting(false);
            return;
          }
          setResult(built);
        } catch (err) {
          setError(err?.message || 'Something went wrong while building the report.');
        } finally {
          setSubmitting(false);
        }
      }, 30);
    },
    [values],
  );

  const summary = result?.bundle?.meta
    ? result.bundle.meta.sampleMode
      ? `Symbolic map for ${result.reportContext?.sampleDisplayName || 'sample'} — timing year ${result.bundle.meta.targetYear}.`
      : `${result.bundle.meta.firstName ? `${result.bundle.meta.firstName} — ` : ''}pattern from birth name and date · timing year ${result.bundle.meta.targetYear}.`
    : '';

  const hero = useMemo(() => {
    if (isSampleViewerMode) {
      return {
        title: 'Study example',
        lead:
          'Structured interpretation from historical birth data. Switch between Edgar and Marie to compare how the same report sections are developed — for study, not biography.',
      };
    }
    return {
      title: 'Your report',
      lead:
        'Enter your birth details to generate your personal numerology reading. Under Study → Sample readings, Edgar and Marie show the same framework in practice.',
    };
  }, [isSampleViewerMode]);

  return (
    <div className="amm-num-page">
      <div className="amm-num-shell">
        <header className="amm-num-hero">
          <p className="amm-num-eyebrow">Astral Moon Magic</p>
          <h1 className="amm-num-h1">{hero.title}</h1>
          <p className="amm-num-lead">{hero.lead}</p>
        </header>

        {!isSampleViewerMode ? (
          <p className="amm-num-samples-link no-print">
            <Link to="/numerology/study/samples">Sample readings for study</Link>
            <span className="amm-num-samples-link__sep" aria-hidden>
              {' · '}
            </span>
            Edgar and Marie — study the structure before you create your report.
          </p>
        ) : null}

        {isSampleViewerMode && isKnownSample ? (
          <div className="amm-num-sample-switch no-print">
            <span className="amm-num-sample-switch-label">Sample:</span>
            <button
              type="button"
              className={
                (activeSampleId || sampleParam) === SAMPLE_PERSONA_IDS.POE ? 'is-active' : ''
              }
              onClick={() => switchSample(SAMPLE_PERSONA_IDS.POE)}
              disabled={submitting}
            >
              Edgar Allan Poe
            </button>
            <button
              type="button"
              className={
                (activeSampleId || sampleParam) === SAMPLE_PERSONA_IDS.CURIE ? 'is-active' : ''
              }
              onClick={() => switchSample(SAMPLE_PERSONA_IDS.CURIE)}
              disabled={submitting}
            >
              Marie Curie
            </button>
          </div>
        ) : null}

        {error ? (
          <div className="amm-num-alert" role="alert" aria-live="assertive">
            {error}
          </div>
        ) : null}

        {!isSampleViewerMode ? (
          <NumerologyReportForm values={values} onChange={onChange} onSubmit={onSubmit} submitting={submitting} />
        ) : null}

        {submitting ? (
          <div className="amm-num-loading" role="status" aria-live="polite">
            <div className="amm-num-spinner" aria-hidden />
            <span>Organizing your pattern…</span>
          </div>
        ) : null}

        {!submitting && result?.ok ? (
          <NumerologyReportView reportModel={result} summary={summary} />
        ) : null}

        {isSampleViewerMode && !submitting && result?.ok ? (
          <section className="amm-num-sample-own-cta no-print" aria-labelledby="amm-num-own-cta-heading">
            <h2 id="amm-num-own-cta-heading" className="amm-num-sample-own-cta__title">
              Continue with your own chart
            </h2>
            <p className="amm-num-sample-own-cta__text">
              These study examples illustrate structure and tone. When you are ready, generate your report from your own
              birth name and date and review it with the same understanding.
            </p>
            <div className="amm-num-sample-own-cta__actions">
              <Link className="amm-num-sample-own-cta__btn amm-num-sample-own-cta__btn--primary" to="/numerology/report">
                Your report
              </Link>
              <Link className="amm-num-sample-own-cta__btn amm-num-sample-own-cta__btn--secondary" to="/numerology/study/samples">
                Study samples
              </Link>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
